import React, { useEffect, useMemo, useState } from 'react';
import api from './api';
import './styles/index.css';
import './RecaudadorDashboard.css';

const RecaudadorDashboard = ({ onBack, onLogout }) => {
  const user = api.getCurrentUser();
  const [bares, setBares] = useState([]);
  const [barId, setBarId] = useState('');
  const [ligas, setLigas] = useState([]);
  const [ligaId, setLigaId] = useState('');
  const [partidasPagadas, setPartidasPagadas] = useState('');
  const [loadingBares, setLoadingBares] = useState(true);
  const [loadingLigas, setLoadingLigas] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.getRecaudacionBares()
      .then(data => {
        if (cancelled) return;
        setBares(data);
        setBarId(data[0]?._id || '');
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'No se pudieron cargar los bares');
      })
      .finally(() => {
        if (!cancelled) setLoadingBares(false);
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!barId) {
      setLigas([]);
      setLigaId('');
      return undefined;
    }

    let cancelled = false;
    setLoadingLigas(true);
    setError('');
    setNotice('');
    api.getLigasControlRecaudacion(barId)
      .then(data => {
        if (cancelled) return;
        setLigas(data);
        setLigaId(current => data.some(liga => liga.ligaId === current) ? current : (data[0]?.ligaId || ''));
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'No se pudieron cargar las ligas');
      })
      .finally(() => {
        if (!cancelled) setLoadingLigas(false);
      });

    return () => { cancelled = true; };
  }, [barId]);

  const ligaActual = useMemo(
    () => ligas.find(liga => liga.ligaId === ligaId) || null,
    [ligas, ligaId]
  );

  const handleSave = async event => {
    event.preventDefault();
    if (!ligaActual || partidasPagadas === '') return;

    setSaving(true);
    setError('');
    setNotice('');
    try {
      const updated = await api.guardarVisitaRecaudacion(ligaActual.ligaId, Number(partidasPagadas));
      setLigas(current => current.map(liga => liga.ligaId === updated.ligaId ? updated : liga));
      setPartidasPagadas('');
      setNotice('Visita guardada. El pendiente queda actualizado para la próxima vez.');
    } catch (err) {
      setError(err.message || 'No se pudo guardar la visita');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    api.logout();
    onLogout?.();
  };

  return (
    <main className="recaudador-dashboard">
      <header className="dashboard-header dashboard-header-pro recaudador-header">
        <div className="header-brand">
          <img src="/Logo26.png" alt="501 Dart League" className="dashboard-logo" />
          <div className="header-brand-text">
            <h1>Control de partidas</h1>
            <span className="header-brand-subtitle">Hola, {user?.username || user?.nombre || 'recaudador'}</span>
          </div>
        </div>
        <div className="header-actions">
          {onBack && (
            <button type="button" className="btn-action btn-outline-action" onClick={onBack}>
              ← Inicio
            </button>
          )}
          <button type="button" className="btn-action btn-secondary-action" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </div>
      </header>

      {error && <div className="error-message" role="alert">{error}</div>}
      {notice && <div className="recaudador-notice" role="status">{notice}</div>}

      <section className="recaudador-controls" aria-label="Seleccionar bar y liga">
        <label>
          Bar
          <select value={barId} onChange={event => setBarId(event.target.value)} disabled={loadingBares || bares.length === 0}>
            {loadingBares && <option value="">Cargando bares...</option>}
            {!loadingBares && bares.length === 0 && <option value="">No hay bares disponibles</option>}
            {bares.map(bar => <option key={bar._id} value={bar._id}>{bar.nombre} ({bar.username})</option>)}
          </select>
        </label>

        <label>
          Liga
          <select value={ligaId} onChange={event => { setLigaId(event.target.value); setPartidasPagadas(''); }} disabled={loadingLigas || ligas.length === 0}>
            {loadingLigas && <option value="">Cargando ligas...</option>}
            {!loadingLigas && ligas.length === 0 && <option value="">Este bar no tiene ligas disponibles</option>}
            {ligas.map(liga => <option key={liga.ligaId} value={liga.ligaId}>{liga.nombre}</option>)}
          </select>
        </label>
      </section>

      {ligaActual && (
        <>
          <section className="recaudador-summary" aria-label="Resumen de partidas">
            <div className="recaudador-stat">
              <span>Nuevas desde la última visita</span>
              <strong>{ligaActual.nuevasDesdeUltimaVisita}</strong>
            </div>
            <div className="recaudador-stat">
              <span>Pendientes anteriores</span>
              <strong>{ligaActual.pendientesAnteriores}</strong>
            </div>
            <div className="recaudador-stat recaudador-stat-total">
              <span>Total por cobrar</span>
              <strong>{ligaActual.pendientesTotales}</strong>
            </div>
          </section>

          <form className="recaudador-visit" onSubmit={handleSave}>
            <div>
              <h2>¿Cuántas partidas te pagaron en esta visita?</h2>
              <p>Se han completado {ligaActual.totalPartidas} partidas en total. Puedes registrar un pago parcial.</p>
            </div>
            <div className="recaudador-visit-controls">
              <input
                aria-label="Partidas pagadas en esta visita"
                type="number"
                min="0"
                max={ligaActual.pendientesTotales}
                step="1"
                inputMode="numeric"
                value={partidasPagadas}
                onChange={event => setPartidasPagadas(event.target.value)}
                required
              />
              <span>de {ligaActual.pendientesTotales}</span>
              <button type="submit" className="btn-action btn-primary-action" disabled={saving || partidasPagadas === '' || ligaActual.pendientesTotales === 0}>
                {saving ? 'Guardando...' : 'Guardar visita'}
              </button>
            </div>
            {ligaActual.ultimaVisita && (
              <small>Último registro: {new Date(ligaActual.ultimaVisita).toLocaleDateString('es-ES')}</small>
            )}
          </form>

          <section className="recaudador-history" aria-label="Historial de visitas">
            <h2>Visitas anteriores</h2>
            {ligaActual.visitas?.length > 0 ? (
              <ul>
                {ligaActual.visitas.map((visita, index) => (
                  <li key={`${visita.fecha}-${index}`}>
                    <span className="recaudador-history-date">
                      {new Date(visita.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                    <span>
                      Te pagaron <strong>{visita.partidasPagadas}</strong> {visita.partidasPagadas === 1 ? 'partida' : 'partidas'}
                    </span>
                    <small>
                      {visita.pendientesTras === 0
                        ? 'sin pendientes'
                        : `quedaron ${visita.pendientesTras} pendiente${visita.pendientesTras === 1 ? '' : 's'}`}
                    </small>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Aún no hay visitas registradas en esta liga.</p>
            )}
          </section>
        </>
      )}
    </main>
  );
};

export default RecaudadorDashboard;